import React, { useState, useMemo, memo, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import { FixedSizeGrid as Grid } from 'react-window';

import BlackContainer from 'components/Common/BlackContainer';
import LinkOperationCard from 'components/Cards/LinkOperationCard';
import Page from 'xpi-black-ui/Base/Page';
import Wrapper from 'xpi-black-ui/Base/Wrapper';
import Loading from 'xpi-black-ui/Base/Loading';
import { P } from 'xpi-black-ui/Base/Typography';
import { convertNumberBR } from 'xpi-black-ui/Utils/helpers';

import useFetchOperationsWithFilter from 'store/Operations/queries/useOperationsWithFilter';
import useOperationStore from 'store/Operations/state';
import { moneyFormatter } from 'utils/helpers';


import OperationFilter from './OperationFilter';
import OperationOrderByMenu from './OperationOrderByMenu';

import Pagination, { SectionTitle, MainContainer, OperationContainer, CardContainer } from './styles';

const DEFAULT_FILTERED_OPTIONS_STATE = {
  CategoriasAtivo: [],
  Ativos: [],
  TiposEstrategia: [],
  Estruturas: [],
  Vencimentos: [],
  PapelIncluso: [],
};

const Container = memo(({ isOperationDispatchLoading }) => {
  const useHistoryRouter = useHistory();

  // const HOOK_USE_OPERATIONS = useOperations();
  const [_, setFakerOperationsOrderBy] = useState([]);
  const [type, setType] = useState('prateleira');
  const [currentPage, setCurrentPage] = useState(1);
  // const [datad, setData] = useState([]);
  // const [dataFilter, setDataFilter] = useState(null);
  const isOperationShelfType = useMemo(() => type === 'prateleira', [type]);

  const [filteredOptions, setFilteredOptions] = useState(DEFAULT_FILTERED_OPTIONS_STATE);

  const menu = useMemo(() => {
    const { pathname } = useHistoryRouter.location;

    if (pathname.search('equities') !== -1) return 'equities';
    if (pathname.search('fx') !== -1) return 'fx';
    if (pathname.search('commodities') !== -1) return 'commodities';
    if (pathname.search('juros') !== -1) return 'juros';
    if (pathname.search('indices') !== -1) return 'indices';
    if (pathname.search('novasoperacoes') !== -1) return 'novas operações';

    return 'equities';
  }, [useHistoryRouter.location.pathname]);

  const { getOperationsCount, shelfCount, exclusivesCount } = useOperationStore();

  useEffect(() => {
    getOperationsCount(menu);
  }, [menu]);

  const { data, dataFilter, isLoading, pageCount } = useFetchOperationsWithFilter(
    {
      PaginaAtual: currentPage,
      TipoOperacao: type,
      Menu: menu,
      filters: filteredOptions,
    },
  );
  const hasData = useMemo(() => data?.length > 0, [data]);

  const getFilters = useCallback(() => dataFilter, [dataFilter]);

  const title = useMemo(() => `Operações Disponíveis ${`- ${menu}` || ''}`, [menu]);
  const hasOperation = useMemo(() => !isLoading && hasData, [isLoading, hasData]);
  const notHasOperation = useMemo(() => !isLoading && !hasData, [isLoading, hasData]);

  const maxProfitFormatted = useCallback(
    (maxProfit, currency, prefix) => (maxProfit === -1 ? 'Ilimitado' : moneyFormatter(maxProfit, currency, prefix)), [],
  );
  const maxLossFormatted = useCallback(
    (maxLoss, currency, prefix) => (maxLoss === -1 ? 'Ilimitado' : moneyFormatter(maxLoss, currency, prefix)), [],
  );
  const comissionFormatted = useCallback(
    (assetName, comission, pctComission) => (assetName === 'IDI' ? `R$ ${convertNumberBR(comission)}` : `${convertNumberBR(pctComission, 2)} %`),
    [],
  );
  const priceFormatted = useCallback(
    (assetName, price, priceReference, multiplicator) => (assetName === 'IDI' ? `R$ ${convertNumberBR(price)}` : `${convertNumberBR((price * multiplicator / (priceReference * multiplicator)) * 100, 2)}%`),
    [],
  );


  const scrollToTop = () => window.scrollTo(0, 0);

  const handleOnPageChange = ({ selected }) => {
    setCurrentPage(selected + 1);
    scrollToTop();
  };

  const getAssetImageByTicker = useCallback((ticker) => {
    const OPERATIONS_IMAGES_STORAGE = JSON.parse(window.localStorage.getItem('assetsImages'));
    const HAS_OPERATION_IMAGE_STORAGE = OPERATIONS_IMAGES_STORAGE?.length > 0;

    if (HAS_OPERATION_IMAGE_STORAGE) {
      return OPERATIONS_IMAGES_STORAGE.filter(({ name }) => name === ticker)[0]?.image;
    }

    return ticker;
  }, []);

  const NoOperatioMessage = () => (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <P style={{ marginBottom: '16px' }}>No momento não há operações disponíveis nesta página. </P>
      <P>Para mais informações, favor entrar em contato com a mesa de operações.</P>
    </div>
  );

  const transformToMatrix = (list, elementsPerSubArray) => {
    const matrix = [];

    for (let i = 0; i < list.length; i += elementsPerSubArray) {
      matrix.push(list.slice(i, i + elementsPerSubArray));
    }

    return matrix;
  };

  const getCard = (item) => {
    const {
      operacaoId,
      ativo,
      estrutura,
      estrategia,
      isHighlight,
      tipoEstrategia,
      lucroMaximo,
      prejuizoMaximo,
      vencimento,
      objetivo,
      comissao,
      pctComissao,
      precoOperacao,
      precoReferencia,
      multiplicador,
      percentualPrecoUnitarioCliente,
      codigoCamaraCompensasao,
    } = item;

    if (menu !== 'novas operações' && !Number.isNaN(Number(item.operacaoId))) {
      return (
        <LinkOperationCard
          key={operacaoId}
          id={operacaoId.toString()}
          data={{
            isHighlight,
            tagList: [tipoEstrategia],
            logoSRC: getAssetImageByTicker(ativo.nome),
            title: ativo.nome,
            codigoCamaraCompensasao,
            subtitle: estrutura.nome,
            maxProfit: maxProfitFormatted(lucroMaximo, lucroMaximo.unidade, lucroMaximo.simbolo),
            maxLoss: maxLossFormatted(prejuizoMaximo),
            commission: comissionFormatted(ativo.nome, comissao, pctComissao),
            price: priceFormatted(ativo.nome, precoOperacao, precoReferencia, multiplicador),
            closingDate: new Date(vencimento).toISOString().slice(0, 10).split('-')
              .reverse()
              .join('/'),
            objective: objetivo,
          }}
        />
      );
    }

    return (
      <LinkOperationCard
        key={operacaoId}
        id={operacaoId.toString()}
        data={{
          isHighlight: false,
          tagList: [estrategia],
          logoSRC: `data:image/png;base64,${ativo.imagemBase64}`,
          title: ativo.nome,
          subtitle: tipoEstrategia,
          maxProfit: lucroMaximo ? maxProfitFormatted(lucroMaximo.valor, lucroMaximo.unidade, lucroMaximo.simbolo) : null,
          maxLoss: prejuizoMaximo ? maxLossFormatted(prejuizoMaximo.valor, prejuizoMaximo.unidade, prejuizoMaximo.simbolo) : null,
          commission: `${convertNumberBR(pctComissao, 2)} %`,
          price: `${convertNumberBR(percentualPrecoUnitarioCliente, 2)} %`,
          closingDate: new Date(`${vencimento.replace('Z', '')}-03:00`)
            .toISOString()
            .slice(0, 10)
            .split('-')
            .reverse()
            .join('/'),
          objective: objetivo,
          menu,
        }}
      />
    );
  };

  const renderGrid = (columnIndex, rowIndex, values, style) => {
    const transform = transformToMatrix(values, menu !== 'novas operações' ? 3 : 4);

    const CELL_GAP = 10;

    const newStyle = {
      ...style,
      left: columnIndex === 0 ? style.left : Number(style.left) + columnIndex * CELL_GAP,
      right: columnIndex === (menu !== 'novas operações' ? 3 : 4) ? style.right : Number(style.right) + columnIndex * CELL_GAP,
      top: rowIndex === 0 ? style.top : Number(style.top) + rowIndex * CELL_GAP,
    };

    if (transform[rowIndex][columnIndex]) {
      return (
        <CardContainer style={newStyle}>
          {getCard(transform[rowIndex][columnIndex])}
        </CardContainer>
      );
    }
    return null;
  };

  return (
    <BlackContainer>
      <Page>
        {menu !== 'novas operações' && (
          <OperationFilter
            type={type}
            menu={menu}
            onFilter={filterData => setFilteredOptions(oldState => ({ ...oldState, ...filterData }))}
            onClear={filterData => setFilteredOptions(oldState => ({ ...oldState, ...filterData }))}
            options={{
              Ativos: dataFilter?.ativos.map(item => item.nome) || [],
              CategoriasAtivo: dataFilter?.categoriasAtivo || [],
              Estruturas: dataFilter?.estruturas || [],
              PapelIncluso: [{
                id: 1,
                value: 1,
                label: 'Com a compra da ação',
                isSelected: false,
              },
              {
                id: 2,
                value: 2,
                label: 'Sem a compra da ação',
                isSelected: false,
              }] || [],
              TiposEstrategia: dataFilter?.tiposEstrategia || [],
              Vencimentos: dataFilter?.vencimentos || [],
            }}
          />
        )}

        <Wrapper spaceTop innerWrapper>
          <SectionTitle>{title}</SectionTitle>

          <MainContainer>
            <OperationOrderByMenu
              operations={data || []}
              isOperationShelfType={isOperationShelfType}
              quantityOnShelf={shelfCount}
              quantityOnExclusive={exclusivesCount}
              setOperationType={setType}
              setOperationsOrderBy={setFakerOperationsOrderBy}
              disableOrder={menu === 'novas operações'}
            />

            {(isOperationDispatchLoading || isLoading) && <Loading text="Carregando..." />}

            {((!isOperationDispatchLoading && !isLoading) && hasOperation) && (
              <OperationContainer>
                <Grid
                  columnCount={menu !== 'novas operações' ? 3 : 4}
                  columnWidth={320}
                  rowHeight={512}
                  height={menu !== 'novas operações' ? 1300 : 2600}
                  width={menu !== 'novas operações' ? 1000 : 1334}
                  rowCount={transformToMatrix(data, menu !== 'novas operações' ? 3 : 4).length}
                >
                  {({ columnIndex, rowIndex, style }) => (
                    renderGrid(columnIndex, rowIndex, data, style)
                  )}
                </Grid>

                <Pagination
                  onPageChange={handleOnPageChange}
                  forcePage={currentPage - 1}
                  pageCount={pageCount}
                  pageRangeDisplayed={5}
                  renderOnZeroPageCount={null}
                />
              </OperationContainer>
            )}

            {notHasOperation && <NoOperatioMessage />}
          </MainContainer>
        </Wrapper>
      </Page>
    </BlackContainer>
  );
});

export default Container;
